import React, { Dispatch, useState, useEffect, useCallback, SetStateAction } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Api from '../../../Job/API';
import { classes } from '../styles';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';

export default function PanelWorkflow({
  jobId,
  panelWorkflowTabJobData,
  setPanelWorkflowTabJobData,
  setIsEdited
}: {
  jobId: number;
  panelWorkflowTabJobData: {
    panel_chairs: {
      error: string;
      required: boolean;
      value: { id: number; firstname: string; lastname: string; email: string }[];
    };
    panel_members: {
      error: string;
      required: boolean;
      value: { id: number; firstname: string; lastname: string; email: string }[];
    };
    panel_observers: {
      error: string;
      required: boolean;
      value: { id: number; firstname: string; lastname: string; email: string }[];
    };
  } | null;
  setPanelWorkflowTabJobData: Dispatch<
    SetStateAction<{
      panel_chairs: {
        error: string;
        required: boolean;
        value: { id: number; firstname: string; lastname: string; email: string }[];
      };
      panel_members: {
        error: string;
        required: boolean;
        value: { id: number; firstname: string; lastname: string; email: string }[];
      };
      panel_observers: {
        error: string;
        required: boolean;
        value: { id: number; firstname: string; lastname: string; email: string }[];
      };
    } | null>
  >;
  setIsEdited: Dispatch<SetStateAction<boolean>>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [chairsOptions, setChairsOptions] = useState<
    {
      id: number;
      firstname: string;
      lastname: string;
    }[]
  >([]);

  const fetchWorkflowData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await Api.getJobPanelChairs(jobId);
      setChairsOptions(response?.panel_chairs);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    chairsOptions?.length === 0 && !isLoading && fetchWorkflowData();
  }, [fetchWorkflowData]);

  const getDefaults = (options, defaults) => {
    if (options?.length > 0 && defaults?.length > 0) {
      const ids = defaults.map((item) => item.id);
      const selected = options.filter((option) => ids.indexOf(option?.id) >= 0);
      return selected;
    } else return [];
  };

  return (
    <Box sx={classes.tabContent}>
      <Box id="panel-workflow-title" sx={classes.headerContent}>
        Panel workflow
      </Box>
      <Box sx={{ ...classes.modalFormLine, marginBottom: 3 }}>
        <Box sx={{ position: 'relative', marginBottom: '4px' }}>
          <Autocomplete
            id="panelChairs"
            multiple
            options={chairsOptions}
            getOptionLabel={(option) => `${option?.firstname} ${option?.lastname}`}
            value={getDefaults(chairsOptions, panelWorkflowTabJobData?.panel_chairs?.value)}
            loading={chairsOptions?.length < 1}
            loadingText={isLoading ? 'Loading Panel chairs...' : 'No Panel chairs'}
            sx={{
              ...classes.formAutocomplete,
              minWidth: '250px',
              width: '100%',
              marginTop: '24px'
            }}
            slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Panel chairs"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
            onChange={(event, value) => {
              setPanelWorkflowTabJobData((prev) => {
                if (!prev) return null;
                return {
                  ...prev,
                  panel_chairs: { ...prev.panel_chairs, value }
                };
              });
              setIsEdited(true);
            }}
          />
        </Box>
      </Box>
      <Box sx={{ ...classes.modalFormLine, marginBottom: 3 }}>
        <Box sx={{ position: 'relative', marginBottom: '4px' }}>
          <Autocomplete
            id="panelMembers"
            multiple
            options={chairsOptions}
            getOptionLabel={(option) => `${option?.firstname} ${option?.lastname}`}
            value={getDefaults(chairsOptions, panelWorkflowTabJobData?.panel_members?.value)}
            loading={chairsOptions?.length < 1}
            loadingText={isLoading ? 'Loading Panel members...' : 'No Panel members'}
            sx={{
              ...classes.formAutocomplete,
              minWidth: '250px',
              width: '100%',
              marginTop: '24px'
            }}
            slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Panel members"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
            onChange={(event, value) => {
              setPanelWorkflowTabJobData((prev) => {
                if (!prev) return null;
                return {
                  ...prev,
                  panel_members: { ...prev.panel_members, value }
                };
              });
              setIsEdited(true);
            }}
          />
        </Box>
      </Box>
      <Box sx={{ ...classes.modalFormLine, marginBottom: 3 }}>
        <Box sx={{ position: 'relative', marginBottom: '4px' }}>
          <Autocomplete
            id="panelObservers"
            multiple
            options={chairsOptions}
            getOptionLabel={(option) => `${option?.firstname} ${option?.lastname}`}
            value={getDefaults(chairsOptions, panelWorkflowTabJobData?.panel_observers?.value)}
            loading={chairsOptions?.length < 1}
            loadingText={isLoading ? 'Loading Panel observers...' : 'No Panel observers'}
            sx={{
              ...classes.formAutocomplete,
              minWidth: '250px',
              width: '100%',
              marginTop: '24px'
            }}
            slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Panel observers"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
            onChange={(event, value) => {
              setPanelWorkflowTabJobData((prev) => {
                if (!prev) return null;
                return {
                  ...prev,
                  panel_observers: { ...prev.panel_observers, value }
                };
              });
              setIsEdited(true);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
